import React, { useMemo } from 'react';
import './tablePagination.scss';

/**
 * Builds page list with ellipsis, e.g. [1, 2, 3, 4, 5, 'ellipsis', 17]
 */
const buildPageItems = (current, total) => {
    const safeTotal = Math.max(1, Number(total) || 1);
    const safeCurrent = Math.min(Math.max(1, Number(current) || 1), safeTotal);

    if (safeTotal <= 7) {
        return Array.from({ length: safeTotal }, (_, i) => i + 1);
    }

    const pages = new Set([1, safeTotal, safeCurrent]);

    for (let i = safeCurrent - 1; i <= safeCurrent + 1; i += 1) {
        if (i > 1 && i < safeTotal) pages.add(i);
    }

    if (safeCurrent <= 4) {
        [2, 3, 4, 5].forEach((p) => {
            if (p < safeTotal) pages.add(p);
        });
    }

    if (safeCurrent >= safeTotal - 3) {
        [safeTotal - 4, safeTotal - 3, safeTotal - 2, safeTotal - 1].forEach((p) => {
            if (p > 1) pages.add(p);
        });
    }

    const sorted = [...pages]
        .filter((p) => p >= 1 && p <= safeTotal)
        .sort((a, b) => a - b);

    const items = [];
    sorted.forEach((page, index) => {
        if (index > 0 && page - sorted[index - 1] > 1) {
            items.push('ellipsis');
        }
        items.push(page);
    });
    return items;
};

/**
 * TablePagination — “Showing X to Y of Z” + Previous / page pills / Next
 */
const TablePagination = ({
    rowsPerPageOptions,
    count,
    rowsPerPage = 20,
    page,
    onChangePage,
    onChangeRowsPerPage,
    rowsPageOption,
    nextPage,
    previousPage,
    currentPage = 1,
    totalPages = 1,
    totalRecords = 0,
    firstPage,
    lastPage,
    goToPage,
    accent = 'red',
    visibleRowCount = 0,
}) => {
    const safeTotalPages = Math.max(1, Number(totalPages) || 1);
    const safeCurrentPage = Math.min(
        Math.max(1, Number(currentPage) || 1),
        safeTotalPages
    );
    const safeRowsPerPage = Math.max(1, Number(rowsPerPage) || 20);
    const safeVisibleRows = Math.max(0, Number(visibleRowCount) || 0);

    const safeTotalRecords = (() => {
        const raw = totalRecords ?? count;
        const parsed = Number(raw);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;

        // API / redux sometimes sends totalPages but null/0 totalRecords
        if (safeTotalPages > 1) {
            if (safeCurrentPage === safeTotalPages && safeVisibleRows > 0) {
                return (safeTotalPages - 1) * safeRowsPerPage + safeVisibleRows;
            }
            return safeTotalPages * safeRowsPerPage;
        }

        if (safeVisibleRows > 0) {
            return safeVisibleRows;
        }

        return 0;
    })();

    const from =
        safeTotalRecords === 0
            ? 0
            : (safeCurrentPage - 1) * safeRowsPerPage + 1;
    const to =
        safeTotalRecords === 0
            ? 0
            : Math.min(safeCurrentPage * safeRowsPerPage, safeTotalRecords);

    const pageItems = useMemo(
        () => buildPageItems(safeCurrentPage, safeTotalPages),
        [safeCurrentPage, safeTotalPages]
    );

    const handleRowsPerPageChange = (event) => {
        if (onChangeRowsPerPage) onChangeRowsPerPage(event);
    };

    const selectPage = (pageNum) => {
        if (pageNum === safeCurrentPage) return;
        if (typeof goToPage === 'function') {
            goToPage(pageNum);
            return;
        }
        if (pageNum === 1 && typeof firstPage === 'function') {
            firstPage();
            return;
        }
        if (typeof lastPage === 'function') {
            // Prefer page payload (redux lastPage reducers already accept it)
            lastPage(pageNum);
        }
    };

    const isPrevDisabled = safeCurrentPage <= 1;
    const isNextDisabled = safeCurrentPage >= safeTotalPages;

    return (
        <div
            className={`eops-table-pagination eops-table-pagination--accent-${
                accent === 'blue' ? 'blue' : 'red'
            }`}
        >
            {rowsPageOption ? (
                <div className="eops-table-pagination__rows">
                    <span>Rows per page:</span>
                    <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                        {(rowsPerPageOptions || []).map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>
            ) : null}

            <p className="eops-table-pagination__summary">
                Showing {from} to {to} of {safeTotalRecords} entries
            </p>

            <div className="eops-table-pagination__controls" role="navigation" aria-label="Pagination">
                <button
                    type="button"
                    className="eops-table-pagination__nav"
                    onClick={() => previousPage && previousPage()}
                    disabled={isPrevDisabled}
                >
                    <span aria-hidden="true">&lt;</span> Previous
                </button>

                <div className="eops-table-pagination__pages">
                    {pageItems.map((item, index) =>
                        item === 'ellipsis' ? (
                            <span
                                key={`ellipsis-${index}`}
                                className="eops-table-pagination__ellipsis"
                            >
                                ...
                            </span>
                        ) : (
                            <button
                                type="button"
                                key={item}
                                className={`eops-table-pagination__page${
                                    item === safeCurrentPage
                                        ? ' eops-table-pagination__page--active'
                                        : ''
                                }`}
                                onClick={() => selectPage(item)}
                                aria-current={
                                    item === safeCurrentPage ? 'page' : undefined
                                }
                            >
                                {item}
                            </button>
                        )
                    )}
                </div>

                <button
                    type="button"
                    className="eops-table-pagination__nav"
                    onClick={() => nextPage && nextPage()}
                    disabled={isNextDisabled}
                >
                    Next <span aria-hidden="true">&gt;</span>
                </button>
            </div>
        </div>
    );
};

export default TablePagination;
